import {useEffect, useMemo, useRef, useState} from "react";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {BanknotesIcon, PlusIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {ExclamationTriangleIcon} from "@heroicons/react/24/solid";
import {GlaFilterPreset, GLASelector} from "../GLASelector";
import {BANK_GL_ACCOUNT, DEFAULT_GL_ACCOUNT, GlAccount} from "../../../hooks/useGLAccounts.ts";
import {useSubledgerContext} from "../../../contexts/SubledgerContext.tsx";
import {toMoneyFromCents} from "../../../lib/Money.ts";
import {useNotifications} from "../../../lib/NotificationContext.tsx";
import {MoneyLabel} from "../../general/MoneyLabel.tsx";
import {PrimaryButton} from "../../general/PrimaryButton.tsx";
import {SecondaryButton} from "../../general/SecondaryButton.tsx";
import {InfoTooltip} from "../../general/InfoTooltip.tsx";
import {createTable} from "../../table/CreateTable.tsx";
import BankAccountSelector from "../BankAccountSelector.tsx";
import {type BankAccountData, CustomerBankAccount} from "../../../api/customerBankAccounts/CustomerBankAccount.ts";
import {MoneyInput} from "../../general/MoneyInput";
import {amountsForBalance, hasBookableAmount, lineAmountCents, type LineItem} from "./lineItem.ts";
import type {BackendAPITypes} from "../../../generated/blueprint.ts";
import type {
    Booking
} from "../../../pages/app/partner/pages/partnerDashboardPage/components/GeneralJournalBookings/GeneralJournalBookingsTab.model.ts";

interface Props {
    open: boolean;
    onClose: () => void;
    glAccounts: GlAccount[];
    onSuccess?: () => void;
    businessId?: string;
    onSave?: (payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerMakeManualBookingPostRequestBody) => Promise<void>;
    onUpdate?: (payload: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerEditManualBookingPostRequestBody) => Promise<void>;
    onFetchBankAccounts: () => Promise<BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerEntrepreneurGetBankAccountsGetResponseBody>;
    onFetchBankBalance: (bankAccountId: string, date: string) => Promise<{ balance: number | null; iban: string; institution_name: string }>;
    booking?: Booking | null;
    startInEditMode?: boolean;
}

const CURRENCY = "EUR";

const NO_BANK_BALANCE_NOTIFICATION = {
    title: "Geen te boeken banksaldo",
    description: "Het banksaldo kon niet worden bepaald op de gekozen boekingsdatum. Pas de boekingsdatum aan of verwijder de bankregel.",
    type: "error",
} as const;

function bankAccountLineDescription(account: CustomerBankAccount): string {
    const identifier = account.displayIdentifier();
    const institution = account.displayInstitutionName();
    return identifier !== "" && identifier !== institution ? `${institution} — ${identifier}` : institution;
}

let bankLineKeyCounter = 100000;
function newBankLine(): LineItem {
    const key = ++bankLineKeyCounter;
    return {
        id: String(key),
        _key: key,
        gla: BANK_GL_ACCOUNT,
        description: "",
        debit: null,
        credit: null,
        currency: CURRENCY,
        isBankLine: true,
        subledger: null,
    };
}

let keyCounter = 0;
function newLine(): LineItem {
    const key = ++keyCounter;
    return {
        id: String(key),
        _key: key,
        gla: DEFAULT_GL_ACCOUNT,
        description: "",
        debit: null,
        credit: null,
        currency: CURRENCY,
        subledger: null,
    };
}

function computeTotals(lines: LineItem[]): Record<string, { debit: number; credit: number }> {
    const totals: Record<string, { debit: number; credit: number }> = {};
    for (const line of lines) {
        const cur = line.currency;
        if (!totals[cur]) totals[cur] = { debit: 0, credit: 0 };
        totals[cur].debit += lineAmountCents(line.debit);
        totals[cur].credit += lineAmountCents(line.credit);
    }
    return totals;
}

function isBalanced(totals: Record<string, { debit: number; credit: number }>): boolean {
    return Object.values(totals).every(({ debit, credit }) => debit === credit && debit > 0);
}

function lineHasSelectedGla(line: LineItem): boolean {
    if (line.isBankLine) return line.gla.id === BANK_GL_ACCOUNT.id;
    return line.gla.id !== DEFAULT_GL_ACCOUNT.id;
}

const READONLY_INPUT = "w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed";
const READONLY_LINE_INPUT = "w-full rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-500 cursor-not-allowed";

export default function CreateManualBookingModal({
                                                     open,
                                                     onClose,
                                                     onSuccess,
                                                     businessId,
                                                     glAccounts,
                                                     onSave,
                                                     onUpdate,
                                                     onFetchBankAccounts,
                                                     onFetchBankBalance,
                                                     booking,
                                                     startInEditMode,
                                                 }: Props) {
    const { canUseSubledgers } = useSubledgerContext();
    const [isEditing, setIsEditing] = useState(false);
    const mode: 'create' | 'view' | 'edit' = isEditing ? 'edit' : booking ? 'view' : 'create';

    const { dispatch } = useNotifications();
    const [bookingDate, setBookingDate] = useState<string>(new Date().toISOString().slice(0, 10));
    const [auditReason, setAuditReason] = useState<string>("");
    const [lines, setLines] = useState<LineItem[]>([newLine(), newLine()]);
    const [isSaving, setIsSaving] = useState(false);
    const [bankAccounts, setBankAccounts] = useState<CustomerBankAccount[]>([]);

    const linesRef = useRef(lines);
    linesRef.current = lines;

    const readOnlyRef = useRef(mode === 'view');
    readOnlyRef.current = mode === 'view';

    function updateLine(key: number, patch: Partial<LineItem>) {
        setLines(prev => prev.map(l => l._key === key ? { ...l, ...patch } : l));
    }
    const updateLineRef = useRef(updateLine);
    updateLineRef.current = updateLine;

    function removeLine(key: number) {
        setLines(prev => prev.filter(l => l._key !== key));
    }
    const removeLineRef = useRef(removeLine);
    removeLineRef.current = removeLine;

    async function selectBankAccount(key: number, account: CustomerBankAccount) {
        updateLine(key, {
            bankAccountId: account.id,
            description: bankAccountLineDescription(account),
            debit: null,
            credit: null,
        });

        try {
            const result = await onFetchBankBalance(account.id, bookingDate);
            if (!hasBookableAmount(result.balance)) {
                dispatch(NO_BANK_BALANCE_NOTIFICATION);
                return;
            }
            updateLine(key, amountsForBalance(result.balance, CURRENCY));
        } catch {
            dispatch(NO_BANK_BALANCE_NOTIFICATION);
        }
    }
    const selectBankAccountRef = useRef(selectBankAccount);
    selectBankAccountRef.current = selectBankAccount;

    const canRemoveLines = lines.length > 2;

    const LinesTable = useMemo(() => createTable<LineItem>([], {
        columns: [
            {
                name: mode === 'view' ? "Grootboekrekening" : "Grootboekrekening *",
                field: "gla",
                style: { widthMultiplier: 2 },
                disableSorting: true,
                customRender: (line: LineItem) => (
                    <GLASelector
                        accounts={glAccounts}
                        value={line.isBankLine ? line.gla : (line.gla.id === DEFAULT_GL_ACCOUNT.id ? null : line.gla)}
                        onChange={(gla) =>
                            updateLineRef.current(line._key, { gla: gla ?? DEFAULT_GL_ACCOUNT })
                        }
                        filterPreset={GlaFilterPreset.ALL}
                        placeholder="Zoek rekening..."
                        size="xs"
                        disabled={readOnlyRef.current || line.isBankLine}
                        selectedSubledger={canUseSubledgers ? (line.subledger ?? null) : undefined}
                        onSubledgerChange={
                            canUseSubledgers
                                ? (subledger) => updateLineRef.current(line._key, { subledger })
                                : undefined
                        }
                    />
                ),
            },
            {
                name: "Omschrijving",
                field: "description",
                style: { widthMultiplier: 2 },
                disableSorting: true,
                customRender: (line: LineItem) => line.isBankLine && !readOnlyRef.current && !line.originalId ? (
                    <div className="[&_button]:h-8 [&_button]:rounded [&_button]:border [&_button]:border-gray-200 [&_button]:px-2">
                        <BankAccountSelector
                            bankAccounts={bankAccounts}
                            selectedAccountId={line.bankAccountId}
                            onSelect={account => account && selectBankAccountRef.current(line._key, account)}
                            variant="field"
                            allOptionLabel="Selecteer bankrekening..."
                            menuClassName="w-[max(var(--button-width),26rem)]"
                        />
                    </div>
                ) : (
                    <input
                        type="text"
                        value={line.description}
                        onChange={e => updateLineRef.current(line._key, { description: e.target.value })}
                        readOnly={readOnlyRef.current}
                        placeholder={readOnlyRef.current ? "" : "Omschrijving regel"}
                        disabled={line.isBankLine}
                        className={readOnlyRef.current
                            ? READONLY_LINE_INPUT
                            : `w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary ${line.isBankLine ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`
                        }
                    />
                ),
            },
            {
                name: "Debet",
                field: "debit",
                style: { widthMultiplier: 0.8 },
                disableSorting: true,
                customRender: (line: LineItem) => (
                    <MoneyInput
                        value={line.debit}
                        currency={line.currency}
                        allowEmpty
                        placeholder="0,00"
                        readOnly={readOnlyRef.current}
                        disabled={line.isBankLine}
                        onChange={value => updateLineRef.current(line._key, { debit: value, credit: value ? null : line.credit })}
                        className={readOnlyRef.current
                            ? READONLY_LINE_INPUT + " text-right"
                            : `h-8 rounded border border-gray-200 px-2 text-right ${line.isBankLine ? 'bg-gray-50 text-gray-500' : ''}`
                        }
                    />
                ),
            },
            {
                name: "Credit",
                field: "credit",
                style: { widthMultiplier: 0.8 },
                disableSorting: true,
                customRender: (line: LineItem) => (
                    <MoneyInput
                        value={line.credit}
                        currency={line.currency}
                        allowEmpty
                        placeholder="0,00"
                        readOnly={readOnlyRef.current}
                        disabled={line.isBankLine}
                        onChange={value => updateLineRef.current(line._key, { credit: value, debit: value ? null : line.debit })}
                        className={readOnlyRef.current
                            ? READONLY_LINE_INPUT + " text-right"
                            : `h-8 rounded border border-gray-200 px-2 text-right ${line.isBankLine ? 'bg-gray-50 text-gray-500' : ''}`
                        }
                    />
                ),
            },
            {
                name: "",
                field: "id",
                style: { widthMultiplier: 0.2 },
                disableSorting: true,
                customRender: (line: LineItem) => readOnlyRef.current ? null : (
                    <button
                        onClick={() => removeLineRef.current(line._key)}
                        disabled={!canRemoveLines}
                        className="text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                ),
            },
        ],
        options: { disableTopPaginationWidget: true, disableBottomPaginationWidget: true },
    }), [glAccounts, mode, canRemoveLines, bankAccounts, canUseSubledgers]);

    function prefillFromBooking(bookingToLoad: Booking) {
        setBookingDate(bookingToLoad.booking_date);
        setAuditReason(bookingToLoad.audit_reason);
        setLines(bookingToLoad.lines.map((line) => {
            const gla = glAccounts.find(a => a.id === line.general_ledger_account_id) ?? DEFAULT_GL_ACCOUNT;
            return {
                id: line.id,
                originalId: line.id,
                _key: ++keyCounter,
                gla,
                description: line.description ?? "",
                isBankLine: line.general_ledger_account_id === BANK_GL_ACCOUNT.id,
                debit: line.debit_amount_cents !== 0 ? toMoneyFromCents(line.debit_amount_cents, line.currency) : null,
                credit: line.credit_amount_cents !== 0 ? toMoneyFromCents(line.credit_amount_cents, line.currency) : null,
                currency: line.currency,
                subledger: line.subledger ?? null,
            };
        }));
    }

    useEffect(() => {
        if (!open || booking) return;
            setBookingDate(new Date().toISOString().slice(0, 10));
            setAuditReason("");
            setLines([newLine(), newLine()]);
            setIsEditing(false);
    }, [open, booking]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;

        async function loadBankAccounts() {
            try {
                const accounts = await onFetchBankAccounts();
                if (cancelled) return;
                setBankAccounts(accounts
                    .map(account => CustomerBankAccount.fromApiResponse(account as BankAccountData))
                    .filter(account => !account.isCreditCard()));
            } catch {
                if (cancelled) return;
                setBankAccounts([]);
                dispatch({
                    title: "Bankrekeningen niet opgehaald",
                    description: "De bankrekeningen konden niet worden geladen. Sluit het venster en probeer het opnieuw.",
                    type: "error",
                });
            }
        }
        loadBankAccounts();

        return () => { cancelled = true; };
    }, [open]);

    useEffect(() => {
        if (!open || !booking) return;
        prefillFromBooking(booking);
    }, [open, booking, glAccounts]);

    useEffect(() => {
        if (!open || !booking) return;
        setIsEditing(!!startInEditMode);
    }, [open, booking]);

    const [isRefreshingBankLines, setIsRefreshingBankLines] = useState(false);

    const fetchBankBalanceRef = useRef(onFetchBankBalance);
    fetchBankBalanceRef.current = onFetchBankBalance;

    useEffect(() => {
        if (!open || mode === 'view' || !bookingDate) return;
        const bankLines = linesRef.current.filter(l => l.isBankLine && l.bankAccountId);
        if (bankLines.length === 0) {
            setIsRefreshingBankLines(false);
            return;
        }

        let cancelled = false;
        setIsRefreshingBankLines(true);

        async function refreshBankLines() {
            const balances = await Promise.all(bankLines.map(line =>
                fetchBankBalanceRef.current(line.bankAccountId!, bookingDate)
                    .then(result => result.balance)
                    .catch(() => null)
            ));
            if (cancelled) return;

            setIsRefreshingBankLines(false);

            const balanceByKey = new Map(bankLines.map((line, index) => [line._key, balances[index]]));
            setLines(prev => prev.map(line =>
                balanceByKey.has(line._key)
                    ? { ...line, ...amountsForBalance(balanceByKey.get(line._key)!, line.currency) }
                    : line
            ));

            if (balances.some(balance => !hasBookableAmount(balance))) {
                dispatch(NO_BANK_BALANCE_NOTIFICATION);
            }
        }

        refreshBankLines();

        return () => { cancelled = true; };
    }, [bookingDate, open, mode]);

    const totals = computeTotals(lines);
    const balanced = isBalanced(totals);
    const allLinesHaveGla = lines.every(lineHasSelectedGla);
    const allLinesHaveAmount = lines.every(line => lineAmountCents(line.debit) > 0 || lineAmountCents(line.credit) > 0);
    const canSave = balanced && auditReason.trim() !== "" && lines.length >= 2 && allLinesHaveGla && allLinesHaveAmount && !isRefreshingBankLines;

    function addLine() {
        setLines(prev => [...prev, newLine()]);
    }

    function addBankLine() {
        setLines(prev => [...prev, newBankLine()]);
    }

    async function handleSave() {
        if (!canSave || !businessId) return;
        const baseLines = lines.map(l => ({
            general_ledger_account_id: l.gla.id,
            description: l.description.trim() || null,
            debit_amount_cents: lineAmountCents(l.debit),
            credit_amount_cents: lineAmountCents(l.credit),
            currency: l.currency,
            subledger_id: l.subledger?.id ?? null,
        }));
        setIsSaving(true);
        try {
            if (mode === 'edit') {
                if (!onUpdate || !booking) return;
                await onUpdate({
                    client_business_id: businessId,
                    booking_id: booking.id,
                    booking_date: bookingDate,
                    audit_reason: auditReason.trim(),
                    lines: lines.map((l, idx) => ({ ...baseLines[idx], id: l.originalId ?? null })),
                });
                dispatch({ title: "Boeking bijgewerkt", description: "De handmatige boeking is succesvol bijgewerkt.", type: "success" });
            } else {
                if (!onSave) return;
                await onSave({
                    client_business_id: businessId,
                    booking_date: bookingDate,
                    audit_reason: auditReason.trim(),
                    lines: baseLines.map(l => ({ ...l, id: null })),
                });
                dispatch({ title: "Boeking opgeslagen", description: "De handmatige boeking is succesvol aangemaakt.", type: "success" });
            }
            onSuccess?.();
            onClose();
        } catch (err) {
            dispatch({ title: "Fout bij opslaan", description: "De boeking kon niet worden opgeslagen. Probeer het opnieuw.", type: "error" });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} className="relative z-40">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel
                        transition
                        className="relative transform rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 w-full max-w-6xl p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
                                {mode === 'view' ? "Boekingsdetails" : mode === 'edit' ? "Boeking bewerken" : "Nieuwe boeking aanmaken"}
                            </DialogTitle>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {mode === 'view' ? "Boekingsdatum" : "Boekingsdatum *"}
                                </label>
                                <input
                                    type="date"
                                    value={bookingDate}
                                    onChange={e => setBookingDate(e.target.value)}
                                    readOnly={mode === 'view'}
                                    className={mode === 'view'
                                        ? READONLY_INPUT
                                        : "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    }
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Dagboek</label>
                                <input
                                    type="text"
                                    value={booking ? booking.journal : "Manual Booking"}
                                    readOnly
                                    className={READONLY_INPUT}
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {mode === 'view' ? "Reden / audit toelichting" : "Reden / audit toelichting *"}
                                </label>
                                <textarea
                                    value={auditReason}
                                    onChange={e => setAuditReason(e.target.value)}
                                    readOnly={mode === 'view'}
                                    rows={2}
                                    placeholder={mode === 'view' ? "" : "Waarom maak je deze boeking?"}
                                    className={mode === 'view'
                                        ? READONLY_INPUT
                                        : "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                                    }
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-gray-900">Boekingsregels</span>
                                {mode !== 'view' && !balanced && lines.length >= 2 && (
                                    <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                                        Niet in balans
                                    </span>
                                )}
                            </div>

                            <LinesTable data={lines} />

                            {Object.entries(totals).map(([cur, { debit, credit }]) => (
                                <div key={cur} className="flex justify-end gap-8 px-4 py-2 bg-gray-50 border border-t-0 border-gray-200 text-sm font-semibold rounded-b-md">
                                    <span className="text-gray-500 mr-auto">Totaal {cur}</span>
                                    <span
                                        className={mode !== 'view' && debit !== credit ? "text-red-600" : "text-gray-900"}>
                                        D: <MoneyLabel value={debit / 100} currency={cur} />
                                    </span>
                                    <span
                                        className={mode !== 'view' && debit !== credit ? "text-red-600" : "text-gray-900"}>
                                        C: <MoneyLabel value={credit / 100} currency={cur} />
                                    </span>
                                </div>
                            ))}

                            {mode !== 'view' && (
                                <div className="mt-2 flex items-center gap-3">
                                    <SecondaryButton
                                        title="Regel toevoegen"
                                        icon={PlusIcon}
                                        onClick={addLine}
                                    />
                                    <SecondaryButton
                                        title="Bankregel toevoegen"
                                        icon={BanknotesIcon}
                                        onClick={addBankLine}
                                    />
                                    <InfoTooltip
                                        text="In PocketCFO worden bankrekeningen in het grootboek elke dag gesynchroniseerd met het werkelijke banksaldo. Daarom is het niet mogelijk om direct boekingen op de bank-grootboekrekening te doen. Gebruik in plaats daarvan deze knop om een bankrekeningregel aan deze boeking toe te voegen, met het saldo op de boekingsdatum."
                                        position="right"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                            {mode === 'view' ? (
                                <>
                                    <SecondaryButton title="Sluiten" onClick={onClose}/>
                                    <PrimaryButton title="Bewerken" onClick={() => setIsEditing(true)}/>
                                </>
                            ) : (
                                <>
                                    <SecondaryButton
                                        title="Annuleren"
                                        onClick={() => {
                                            if (mode === 'edit' && booking) {
                                                prefillFromBooking(booking);
                                                setIsEditing(false);
                                            } else {
                                                onClose();
                                            }
                                        }}
                                    />
                                    <PrimaryButton
                                        title={isSaving ? "Opslaan..." : mode === 'edit' ? "Wijzigingen opslaan" : "Boeking opslaan"}
                                        onClick={handleSave}
                                        disabled={!canSave || isSaving}
                                        disabledFeedback={
                                            isRefreshingBankLines
                                                ? "Het banksaldo wordt bijgewerkt voor de gekozen boekingsdatum"
                                                : !allLinesHaveGla
                                                ? "Selecteer een grootboekrekening voor elke regel"
                                                : !allLinesHaveAmount
                                                ? "Vul een debet- of creditbedrag in op elke regel"
                                                : !balanced
                                                    ? "De boeking moet in balans zijn"
                                                    : auditReason.trim() === ""
                                                        ? "Vul een reden / audit toelichting in"
                                                        : undefined
                                        }
                                    />
                                </>
                            )}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
